import React from 'react'
import { dateFns as moment } from '@pcln/date-fns-utils'
import PropTypes from 'prop-types'
import invoke from 'lodash/invoke'
import { Flex, Button, Input } from 'pcln-design-system'
import { ArrowLeft } from 'pcln-icons'
import { AirPricedCalendar } from '@pcln/fly-date-picker'
import { parse, format } from 'date-fns'
import noop from '../../lib/noop'
import { getFlights } from './helpers'
import { CalendarContainer, CalendarCloseBanner, StyledInput } from './DatePickerInput.styles'

class DatePickerInput extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      datePickerFocused: false,
      startDate: props.startDate && format(parse(props.startDate, 'yyyyMMdd', new Date()), 'yyyyMMdd'),
      endDate: props.endDate && format(parse(props.endDate, 'yyyyMMdd', new Date()), 'yyyyMMdd'),
      displayDate: this.formatDisplayDate(props),
    }
    this.checkboxRef = React.createRef()
    this.calendar = React.createRef()

    this.handleCalendarBlur = this.handleCalendarBlur.bind(this)
    this.toggleCalendarFocus = this.toggleCalendarFocus.bind(this)
    this.colorCodeImpression = this.colorCodeImpression.bind(this)
    this.fireColorCodeImpression = false
  }

  UNSAFE_componentWillReceiveProps({ startDate, endDate, isRoundTrip }) {
    if (
      (this.props.isRoundTrip !== isRoundTrip || startDate) &&
      ((isRoundTrip && startDate && endDate) || (!isRoundTrip && startDate))
    ) {
      this.setState({
        startDate,
        endDate,
        displayDate: this.formatDisplayDate({
          startDate,
          endDate,
          isRoundTrip,
        }),
      })
    }
  }

  /**
   * Returns the number of full days between the given start and end times. If both times are 12am, this will
   * give the number of nights between the dates. If either date is missing, returns 0.
   * @param startDate
   * @param endDate
   * @returns {number}
   */
  static getNumberNights(startDate, endDate) {
    if (!(startDate && endDate)) {
      return 0
    }

    const start = parse(startDate, 'yyyyMMdd', new Date())
    const end = parse(endDate, 'yyyyMMdd', new Date())

    return Math.floor((end - start) / (1000 * 60 * 60 * 24))
  }

  handleCalendarBlur = () => {
    // Source: https://medium.com/@jessebeach/dealing-with-focus-and-blur-in-a-composite-widget-in-react-90d3c3b49a9b
    this._timeoutID = setTimeout(() => {
      if (this.state.datePickerFocused) {
        this.setState({
          datePickerFocused: false,
        })
      }
    }, 0)
  }

  handleCalendarFocus = () => {
    clearTimeout(this._timeoutID)
    if (!this.state.datePickerFocused) {
      this.setState({
        datePickerFocused: true,
      })
    }
  }
  /* istanbul ignore next */
  handleInputClick = (event) => {
    event.preventDefault()
    event.stopPropagation()
    this.toggleCalendarFocus(true)

    this.colorCodeImpression() // AIR-39989
  }

  /* istanbul ignore next */
  colorCodeImpression = () => {
    // AIR-39989: https://priceline.atlassian.net/browse/AIR-39989

    if (!this.fireColorCodeImpression) {
      this.fireColorCodeImpression = true
      this.props?.pclnExpt?.fireImpression('AIR_SEARCH_LISTINGS_FLEXDATE_CALENDAR')
    }
  }

  handleInputFocus = (event) => {
    event.preventDefault()
    this.props.onFocus()
    this.toggleCalendarFocus(true)
  }

  /* istanbul ignore next */
  handleSelectedDates = (event, datesSelected) => {
    const startDate = datesSelected.startDate ? parse(datesSelected.startDate, 'yyyyMMdd', new Date()) : null
    const endDate = datesSelected.endDate ? parse(datesSelected.endDate, 'yyyyMMdd', new Date()) : null

    this.setState({
      startDate: startDate ? format(startDate, 'yyyyMMdd') : null,
      endDate: endDate ? format(endDate, 'yyyyMMdd') : null,
    })

    // Only commit changes to store and update display date if one or both dates are selected (depending on trip type)
    if ((this.props.isRoundTrip && startDate && endDate) || (!this.props.isRoundTrip && startDate)) {
      this.props.onChange({
        startDate,
        endDate,
      })
    }

    this.setState({
      displayDate: this.formatDisplayDate({
        isRoundTrip: this.props.isRoundTrip,
        startDate,
        endDate,
      }),
    })

    if (this.props.isRoundTrip) {
      if (startDate && endDate) {
        this.closeCalendar()
      }
    } else if (startDate) {
      this.closeCalendar()
    }
  }

  closeCalendar = () => {
    this.toggleCalendarFocus(false)
  }

  toggleCalendarFocus = (value) => {
    this.setState({ datePickerFocused: value }, () => {
      if (!this.state.datePickerFocused) {
        this.props.onBlur()
        invoke(this.checkboxRef, 'focus')
      } else {
        // Add attribute for regression testing
        this.calendar.setAttribute('data-selenium', 'flight-calendar-select')
      }
    })
  }

  formatDisplayDate = ({ isRoundTrip, startDate, endDate }) => {
    // Don't display value unless one or both dates are selected, depending on trip type
    if (!startDate && !endDate) {
      return ''
    }

    const format = 'ddd, MMM D'
    const displayStart = moment(startDate).format(format)
    const displayEnd = endDate ? moment(endDate).format(format) : ''

    return isRoundTrip ? `${displayStart} - ${displayEnd}` : displayStart
  }

  tripTypeFormat = (tripType) => {
    if (tripType === 'OW') {
      return 'one-way'
    } else if (tripType === 'RT') {
      return 'round-trip'
    }
    return tripType
  }

  render() {
    const { stacked, airports, airportsArray } = this.props
    const placeholder = this.props.isRoundTrip ? 'Departing - Returning' : 'Departing'

    const tripType = this.tripTypeFormat(this.props.tripType)

    const flightDetails =
      this.props.tripType === 'OW' || this.props.tripType === 'RT'
        ? {
            cabinClass: this.props.cabinClass,
            tripType,
            flights: getFlights(airportsArray || Object.values(airports)),
          }
        : null

    return (
      <Flex
        width={[1]}
        onBlur={this.handleCalendarBlur}
        onFocus={this.handleCalendarFocus}
        data-testid='calendar-container'
      >
        <StyledInput
          onClick={this.handleInputClick}
          onFocus={this.handleInputFocus}
          placeholder={placeholder}
          value={this.state.displayDate}
          data-selenium={'flight-calendar'}
          aria-expanded={!!this.state.datePickerFocused}
          pl={'40px'}
        />

        <CalendarContainer
          fullscreen={this.props.fullscreen}
          stacked={stacked}
          open={this.state.datePickerFocused}
        >
          {this.state.datePickerFocused ? (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <div
              ref={(node) => {
                this.calendar = node
              }}
              role='application'
              data-testid='flight-calendar-select'
              onMouseDown={(e) => e.preventDefault()}
            >
              <AirPricedCalendar
                onChange={this.handleSelectedDates}
                startDate={this.state.startDate}
                endDate={this.state.endDate}
                priceMap={this.props.calendarPricingMap}
                selectTwoDates={this.props.isRoundTrip}
                flightDetails={flightDetails}
                firstDate={this.props.firstSelectableDate}
                onDismiss={this.closeCalendar}
                withFooter
                allowSameDay
                useColorCode
              />
              <CalendarCloseBanner color='background.lightest'>
                <Button m={2} onClick={this.closeCalendar}>
                  <ArrowLeft />
                </Button>
                <Input placeholder={placeholder} value={this.state.displayDate} readOnly={true} pl={2} />
              </CalendarCloseBanner>
            </div>
          ) : null}
        </CalendarContainer>
      </Flex>
    )
  }
}

DatePickerInput.defaultProps = {
  isRoundTrip: true,
  isMD: false,
  isValid: true,
  calendarPricingMap: [],
  onFocus: noop,
  fullscreen: false,
  airports: {},
  onBlur: noop,
  focusedCalendar: false,
  cabinClass: 'ECO',
}

DatePickerInput.propTypes = {
  isRoundTrip: PropTypes.bool,
  isMD: PropTypes.bool,
  pclnExpt: PropTypes.shape({
    fireImpression: PropTypes.func,
    seti: PropTypes.object,
  }),
  onChange: PropTypes.func,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  cabinClass: PropTypes.string,
  /** airports is deprecated, please use airportsArray */
  airports: PropTypes.object,
  airportsArray: PropTypes.array,
  tripType: PropTypes.string,
  isValid: PropTypes.bool,
  calendarPricingMap: PropTypes.array,
  firstSelectableDate: PropTypes.instanceOf(Date),
  onFocus: PropTypes.func,
  fullscreen: PropTypes.bool,
  onBlur: PropTypes.func,
  focusedCalendar: PropTypes.bool,
  isLastSlice: PropTypes.bool,
  logFlexibleDatesEvent: PropTypes.func,
  stacked: PropTypes.bool,
}

export default DatePickerInput
